import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const CZ_MONTHS = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
const CZ_DOW = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];

export function OccupancyCalendar({ days, adminOnlyFrom, onDay }) {
  const byDate = useMemo(() => Object.fromEntries(days.map((d) => [d.date, d])), [days]);
  const months = useMemo(() => [...new Set(days.map((d) => d.date.slice(0, 7)))].sort(), [days]);
  const [mi, setMi] = useState(0);
  const month = months[Math.min(mi, months.length - 1)];
  if (!month) return <p className="text-sm text-slate-500">Plán zatím nemá žádné termíny.</p>;
  const [y, m] = month.split("-").map(Number);
  const lead = (new Date(y, m - 1, 1).getDay() + 6) % 7;
  const dim = new Date(y, m, 0).getDate();
  const cells = [...Array(lead).fill(null), ...Array.from({ length: dim }, (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`)];
  return (
    <div className="space-y-3" data-testid="occupancy-calendar">
      <div className="flex items-center justify-between">
        <button onClick={() => setMi(Math.max(0, mi - 1))} disabled={mi === 0} className="h-8 w-8 rounded-full border grid place-items-center disabled:opacity-30" data-testid="occ-prev"><ChevronLeft className="h-4 w-4" /></button>
        <div className="font-semibold text-[#0A2540] capitalize" data-testid="occ-month">{CZ_MONTHS[m - 1]} {y}</div>
        <button onClick={() => setMi(Math.min(months.length - 1, mi + 1))} disabled={mi >= months.length - 1} className="h-8 w-8 rounded-full border grid place-items-center disabled:opacity-30" data-testid="occ-next"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {CZ_DOW.map((d) => <div key={d} className="text-[11px] uppercase tracking-wider text-slate-400 py-1">{d}</div>)}
        {cells.map((iso, i) => {
          if (!iso) return <div key={`e${i}`} />;
          const d = byDate[iso];
          if (!d) return <div key={iso} className="aspect-square rounded-lg text-sm text-slate-300 flex items-center justify-center">{parseInt(iso.slice(8), 10)}</div>;
          const pct = d.total ? d.booked / d.total : 0;
          const adminOnly = adminOnlyFrom && iso >= adminOnlyFrom;
          const bg = pct >= 1 ? "bg-[#0A2540] text-white border-[#0A2540]" : pct >= 0.5 ? "bg-amber-50 border-amber-300 text-[#0A2540]" : "bg-emerald-50 border-emerald-200 text-[#0A2540]";
          return (
            <button key={iso} onClick={() => onDay?.(iso)} data-testid={`occ-day-${iso}`} title={`${d.entrances.join(", ")} · ${d.booked}/${d.total} obsazeno${adminOnly ? " · jen předseda" : ""}`}
              className={`aspect-square rounded-lg border-2 text-sm flex flex-col items-center justify-center relative ${bg} hover:ring-2 hover:ring-[#C5A059]`}>
              {adminOnly && <span className="absolute top-0.5 right-1 text-[9px] font-bold text-[#C5A059]">P</span>}
              <span className="font-semibold">{parseInt(iso.slice(8), 10)}</span>
              <span className="text-[10px] opacity-80">{d.booked}/{d.total}</span>
              <span className="text-[9px] opacity-70 truncate max-w-full px-1">{d.entrances.join(",")}</span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1"><i className="h-3 w-3 rounded bg-emerald-50 border border-emerald-200 inline-block" /> volno</span>
        <span className="inline-flex items-center gap-1"><i className="h-3 w-3 rounded bg-amber-50 border border-amber-300 inline-block" /> z poloviny obsazeno</span>
        <span className="inline-flex items-center gap-1"><i className="h-3 w-3 rounded bg-[#0A2540] inline-block" /> plně obsazeno</span>
        <span className="inline-flex items-center gap-1"><b className="text-[#C5A059]">P</b> jen předseda</span>
        <span>čísla = obsazené / všechny termíny dne · písmeno = vchod</span>
      </div>
    </div>
  );
}
